import React, { useEffect, useRef } from 'react';

import OptionsList from '@decisiv/ui-components/lib/atoms/OptionsList';

/**
 * KeyDS OptionsList with its label clicks made to count once.
 *
 * OptionsList puts the row's click handler on the <li> and then wraps the row's
 * contents — checkbox included — in a <label>. So a click on the row's text
 * reaches the <li> twice: once as itself, and once as the click the <label>
 * forwards to the checkbox it labels. Two toggles net to nothing, which is why
 * only the checkbox itself appeared to work while the label looked dead
 * (reported in QA on PD-4063).
 *
 * Cancelling the click suppresses the forwarded one, since a <label> does not
 * activate its control for an event whose default was prevented. One click then
 * reaches the <li>. Nothing is lost by cancelling: OptionsList's checkbox is
 * controlled, handed `onChange={noop}` and driven entirely by `selected`.
 *
 * The checkbox itself is the exception, and it must stay one. KeyDS BooleanInput
 * gives the mark's own <label> an `onClick` that calls `stopPropagation`, so a
 * click there never reaches the row through React at all — the forwarded click
 * is the only way it counts. Cancelling that one turned the checkbox dead
 * instead, which is the state the first cut of this fix left it in.
 *
 * A native capture listener rather than onClickCapture, because React 16
 * delegates from the document and this has to cancel the event before the
 * browser runs the label's activation behaviour.
 *
 * The second effect below fixes a different fault, in a portal: see its own
 * note.
 */
function SelectableOptionsList(props) {
  const guardRef = useRef(null);
  const { selected } = props;

  useEffect(() => {
    const node = guardRef.current;
    if (!node) return undefined;

    const cancelLabelActivation = (event) => {
      const { target } = event;

      // Only the option rows: the footer's buttons and any input above the list
      // behave as they always did.
      if (!target.closest('li[data-id]')) return;

      // The checkbox and its own label rely on the forwarded click; see above.
      if (target.closest('label[for]') || target.tagName === 'INPUT') return;

      event.preventDefault();
    };

    node.addEventListener('click', cancelLabelActivation, true);
    return () => node.removeEventListener('click', cancelLabelActivation, true);
  }, []);

  /**
   * Writes each row's checkbox DOM property back after every commit.
   *
   * The same React-16 portal defect wizardInputs.jsx works around: inside a
   * portal, a controlled boolean input never gets its DOM property updated, so
   * the row reports the right state through aria-selected while the mark itself
   * still looks ticked. Reduced to:
   *
   *   this list rendered on the page          -> input.checked follows `selected`
   *   the same list inside a Modal or Wizard  -> input.checked never changes
   *
   * That is what made an account in the access modals take two clicks to
   * untick: the first click did untick it, and only the mark disagreed
   * (reported in QA on PD-4677).
   *
   * Driven from `selected` rather than from the rendered aria-selected, so this
   * does not turn OptionsList's markup into an interface. No dependency array,
   * for the same reason wizardInputs has none: the DOM property has to be
   * restored after every commit, not only when this component's props change.
   */
  useEffect(() => {
    const sync = () => {
      const node = guardRef.current;
      if (!node) return;

      const checkedIds = (selected || []).map(String);

      node.querySelectorAll('li[data-id]').forEach((row) => {
        const box = row.querySelector('input[type="checkbox"]');
        if (!box) return;

        const shouldBeChecked = checkedIds.includes(row.dataset.id);
        if (box.checked !== shouldBeChecked) box.checked = shouldBeChecked;
      });
    };

    sync();

    // And again once React has finished the event. Writing the property in the
    // effect alone is not enough: React restores a controlled input from its
    // own value tracker at the end of the dispatch, which is after passive
    // effects run, so the stale value goes straight back. A microtask lands
    // after that restore and before paint.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) sync();
    });

    return () => {
      cancelled = true;
    };
  });

  // display: contents so the wrapper takes part in no layout: both callers size
  // the list against the panel around it.
  return (
    <div ref={guardRef} style={{ display: 'contents' }}>
      <OptionsList {...props} />
    </div>
  );
}

export default SelectableOptionsList;
